/**
 * NotificationItemRouter
 *
 * Selects the per-type notification item component, porting the
 * `switch (notificationType)` in `notification_item.dart:58-415`. Covers BOTH
 * the media-trailing group (Task 11) and the text-only group (Task 12). Mirrors
 * the wallet `TransactionItemRouter.tsx` pattern.
 *
 * The `default` case returns `null`, matching Flutter's empty `Container()` with
 * NO padding (`notification_item.dart:410-415`). The three filtered types
 * (`liveStarted`, `contentAdded`, `earlyAccessContentAdded`) are intentionally
 * absent from the switch — they are dropped upstream (Task 8) and were never
 * rendered in Flutter either (commented out / no case).
 *
 * Purely presentational: the page (Task 15) supplies `currentUserId` (for the
 * exclusive-subscription `actionMaker` branches) and the tap handlers; this
 * component owns no state.
 */
import React from 'react';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { NotificationCountKind } from '@/domain/enums/inbox/notificationCountKind';
import { NotificationMentionKind } from '@/domain/enums/inbox/notificationMentionKind';
import { NotificationPayVideoKind } from '@/domain/enums/inbox/notificationPayVideoKind';
import { NotificationPremiumPackageKind } from '@/domain/enums/inbox/notificationPremiumPackageKind';
import { NotificationRoadmapKind } from '@/domain/enums/inbox/notificationRoadmapKind';
import { NotificationScheduleKind } from '@/domain/enums/inbox/notificationScheduleKind';

// Media-trailing group (Task 11)
import { NotificationCountItem } from './items/NotificationCountItem';
import { NotificationContentGiftItem } from './items/NotificationContentGiftItem';
import { NotificationMentionItem } from './items/NotificationMentionItem';
import { NotificationReportContentItem } from './items/NotificationReportContentItem';
import { NotificationUnlockContentItem } from './items/NotificationUnlockContentItem';
import { NotificationScheduleItem } from './items/NotificationScheduleItem';
import { NotificationPayVideoItem } from './items/NotificationPayVideoItem';
import { NotificationEarlyAccessReceivedItem } from './items/NotificationEarlyAccessReceivedItem';

// Text-only group (Task 12)
import { NotificationExclusiveSubscriptionItem } from './items/NotificationExclusiveSubscriptionItem';
import { NotificationReportUserItem } from './items/NotificationReportUserItem';
import { NotificationIdentityVerificationItem } from './items/NotificationIdentityVerificationItem';
import { NotificationPremiumPackageItem } from './items/NotificationPremiumPackageItem';
import { NotificationCampaignRankingItem } from './items/NotificationCampaignRankingItem';
import { NotificationRoadmapItem } from './items/NotificationRoadmapItem';

export interface NotificationItemRouterProps {
  notification: AppNotificationEntity;
  /** Current user's firebase id, for the exclusive-subscription `actionMaker` branches. */
  currentUserId?: string | null;
  onUserTap?: (userId: string) => void;
  onItemTap?: (notification: AppNotificationEntity) => void;
  onItemMediaTap?: (notification: AppNotificationEntity) => void;
}

export const NotificationItemRouter: React.FC<NotificationItemRouterProps> = ({
  notification,
  currentUserId,
  onUserTap,
  onItemTap,
  onItemMediaTap,
}) => {
  const type = notification.info.notificationType as AppNotificationType;

  switch (type) {
    // --- Media-trailing group (Task 11) ---
    case AppNotificationType.contentLike:
      return (
        <NotificationCountItem
          kind={NotificationCountKind.contentLike}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.contentComment:
      return (
        <NotificationCountItem
          kind={NotificationCountKind.contentComment}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.commentLike:
      return (
        <NotificationCountItem
          kind={NotificationCountKind.commentLike}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.commentReply:
      return (
        <NotificationCountItem
          kind={NotificationCountKind.commentReply}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.contentGift:
      return (
        <NotificationContentGiftItem
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.contentMention:
      return (
        <NotificationMentionItem
          kind={NotificationMentionKind.contentMention}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.commentMention:
      return (
        <NotificationMentionItem
          kind={NotificationMentionKind.commentMention}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.reportContent:
      return (
        <NotificationReportContentItem
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.unlockContent:
      return (
        <NotificationUnlockContentItem
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.scheduleContent:
      return (
        <NotificationScheduleItem
          kind={NotificationScheduleKind.scheduleContent}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.scheduleContentPublished:
      return (
        <NotificationScheduleItem
          kind={NotificationScheduleKind.scheduleContentPublished}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.payPremiumVideo:
      return (
        <NotificationPayVideoItem
          kind={NotificationPayVideoKind.payPremiumVideo}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.payPremiumVideoFailed:
      return (
        <NotificationPayVideoItem
          kind={NotificationPayVideoKind.payPremiumVideoFailed}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.payBoostVideo:
      return (
        <NotificationPayVideoItem
          kind={NotificationPayVideoKind.payBoostVideo}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.payBoostVideoFailed:
      return (
        <NotificationPayVideoItem
          kind={NotificationPayVideoKind.payBoostVideoFailed}
          notification={notification}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );
    case AppNotificationType.earlyAccessContentReceived:
      return (
        <NotificationEarlyAccessReceivedItem
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
          onItemMediaTap={onItemMediaTap}
        />
      );

    // --- Text-only group (Task 12) ---
    case AppNotificationType.exclusiveSubscription:
    case AppNotificationType.exclusiveSubscriptionExpired:
    case AppNotificationType.exclusiveSubscriptionRenewd:
    case AppNotificationType.exclusiveSubscriptionRequested:
    case AppNotificationType.exclusiveSubscriptionRequestCancelled:
    case AppNotificationType.exclusiveSubscriptionRequestAccepted:
    case AppNotificationType.exclusiveSubscriptionRequestDeclined:
    case AppNotificationType.exclusiveSubscriptionFailedInsufficientCoins:
      return (
        <NotificationExclusiveSubscriptionItem
          notification={notification}
          currentUserId={currentUserId}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.reportUser:
      return <NotificationReportUserItem notification={notification} onItemTap={onItemTap} />;
    case AppNotificationType.identityVerification:
      return (
        <NotificationIdentityVerificationItem
          notification={notification}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.premiumPackage:
      return (
        <NotificationPremiumPackageItem
          kind={NotificationPremiumPackageKind.premiumPackage}
          notification={notification}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.premiumPackageExpired:
      return (
        <NotificationPremiumPackageItem
          kind={NotificationPremiumPackageKind.premiumPackageExpired}
          notification={notification}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.campaignRanking:
      return (
        <NotificationCampaignRankingItem notification={notification} onItemTap={onItemTap} />
      );
    case AppNotificationType.roadmapLike:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapLike}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.roadmapDislike:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapDislike}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.roadmapComment:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapComment}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.roadmapCommentReply:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapCommentReply}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.roadmapCommentLike:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapCommentLike}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );
    case AppNotificationType.roadmapDonation:
      return (
        <NotificationRoadmapItem
          kind={NotificationRoadmapKind.roadmapDonation}
          notification={notification}
          onUserTap={onUserTap}
          onItemTap={onItemTap}
        />
      );

    // Flutter `default` → empty Container with no padding. Web → render nothing.
    default:
      return null;
  }
};
